import {
    BLACK,
    DARK_GREEN,
    GREY1,
    GREY2,
    PALE_GREEN,
    SWEGON_GREEN,
    WHITE,
    YELLOW
} from '@swegon-core/uic/dist/src/theme';
import styled from 'styled-components';
import { COLOR_EXTRACT, COLOR_SUPPLY } from '../../../assets/airflowcolors';
import { Airflow } from '../../../enums/airflow';

export type AirflowLineProps = {
    $airflow: Airflow;
    $positionLeft: number;
};

export interface SizeElementContainerProps {
    selected: boolean;
}

export interface SizeElementProps {
    $selected: boolean;
    $inrange: boolean;
    $airflow: Airflow;
    $width?: number;
    $height?: number;
    $marginleft?: number;
}

export interface AirflowIndicatorTextProps {
    $floatDirection: 'right' | 'left';
}

const getLinearGradient = (color: string) => `
    repeating-linear-gradient(
        -45deg,
        rgba(0, 0, 0, 0),
        rgba(0, 0, 0, 0) 3px,
        ${color} 3px,
        ${color} 6px
    );
`;

const getStateColor = (
    selected: boolean,
    $inrange: boolean,
    airflow: Airflow,
    hovered: boolean
) => {
    if (selected)
        return airflow === Airflow.SUPPLY
            ? DARK_GREEN
            : getLinearGradient(DARK_GREEN);
    if (hovered)
        return airflow === Airflow.SUPPLY
            ? SWEGON_GREEN
            : getLinearGradient(SWEGON_GREEN);
    if ($inrange)
        return airflow === Airflow.SUPPLY
            ? PALE_GREEN
            : getLinearGradient(PALE_GREEN);
    return airflow === Airflow.SUPPLY ? `${GREY1}` : getLinearGradient(GREY1);
};

export const SizeHeader = styled.label`
    display: inline-block;
    margin-bottom: 12px;
`;

export const SizeSelectionContainer = styled.div`
    padding: 1rem;
`;

export const SizeSelectionGrid = styled.div`
    position: relative;
    grid-column-start: 2;
    grid-column-end: 3;
    grid-row-start: 1;
    grid-row-end: 3;
    animation: fadein 300ms;
`;

export const InnerSizeSelectionContainer = styled.div`
    position: relative;
`;

export const AirflowLine = styled.div<AirflowLineProps>`
    position: absolute;
    width: 3px;
    height: 100%;
    float: left;
    pointer-events: none;
    box-shadow: 0 0 4px ${BLACK}30;
    -webkit-box-shadow: 0 0 4px ${BLACK}30;
    z-index: 4;
    background-color: ${(props) =>
        props.$airflow === Airflow.SUPPLY ? COLOR_SUPPLY : COLOR_EXTRACT};
    left: ${(props) => `${props.$positionLeft}px`};
`;

export const SizeElementContainer = styled.div<SizeElementContainerProps>`
    margin-bottom: 0.4em;
    color: ${(props) => (props.selected ? WHITE : BLACK)};
`;

export const SizeElement = styled.div<SizeElementProps>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
    outline: none;
    font-size: 1rem;
    cursor: pointer;
    background: ${(props) =>
        getStateColor(props.$selected, props.$inrange, props.$airflow, false)};
    width: ${(props) => (props.$width ? `${props.$width}px` : 'auto')};
    height: ${(props) => (props.$height ? `${props.$height}px` : 'auto')};
    margin-left: ${(props) =>
        props.$marginleft ? `${props.$marginleft}px` : '0'};

    ${SizeElementContainer}:hover & {
        background: ${(props) =>
            getStateColor(
                props.$selected,
                props.$inrange,
                props.$airflow,
                true
            )};
        font-size: 1.1rem;
        z-index: 2;
        opacity: 0.8;
    }
`;

export const PassivHausSizeElement = styled.div<SizeElementProps>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    border: 2px solid ${YELLOW};
    z-index: 4;
    background-color: ${YELLOW}50;
    opacity: 0.5;
    width: ${(props) => (props.$width ? `${props.$width}px` : 'auto')};
    height: ${(props) => (props.$height ? `${props.$height}px` : 'auto')};
    margin-left: ${(props) =>
        props.$marginleft ? `${props.$marginleft}px` : '0'};
`;

export const AirflowIndicatorText = styled.span<AirflowIndicatorTextProps>`
    float: ${(props) => props.$floatDirection};
    color: ${GREY2};
    font-size: 0.875rem;
    font-weight: 200;
`;

export const NoMatchingSizes = styled.div`
    height: fit-content;
    text-align: center;
    align-self: center;
`;

export const HoverLabel = styled.div`
    display: inline-flex;
`;
